// src/components/TimesheetApproverView.jsx
import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import { 
  FaUser, FaBuilding, FaHourglassHalf, FaFileAlt, FaCheckCircle, 
  FaTimes, FaExclamationTriangle, FaInfoCircle
} from "react-icons/fa";

const TimesheetApproverView = ({ timesheet, onClose, isApprover, onAction, currentUserId }) => {
  const modalRef = useRef(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);

  // LOGIC: Check if HR is looking at their own log
  const isOwnTimesheet = timesheet?.staffId === currentUserId;

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    if (timesheet) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden'; 
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [timesheet, onClose]);

  if (!timesheet) return null;

  const getStatusClasses = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-orange-100 text-orange-800 border-orange-200';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div
        ref={modalRef}
        className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-600 to-orange-500 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-2xl">
                <FaFileAlt className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-black text-white uppercase tracking-tight">Timesheet Review</h2>
                <p className="text-orange-100 text-xs font-bold uppercase tracking-widest">ID: {timesheet._id?.slice(-8)}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full text-white transition-colors">
              <FaTimes size={24} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow overflow-y-auto px-8 py-6 space-y-6">
          {timesheet.status === 'rejected' && (
            <div className="bg-red-50 border-2 border-red-100 p-4 rounded-2xl flex items-start gap-3">
              <FaExclamationTriangle className="text-red-500 mt-1" />
              <div>
                <p className="text-sm font-black text-red-800 uppercase">Rejection Reason</p>
                <p className="text-red-600 text-sm">{timesheet.rejectionReason || "No reason specified."}</p>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Employee</p>
              <div className="flex items-center gap-2">
                <FaUser className="text-orange-500" />
                <span className="font-bold text-gray-800">{timesheet.name}</span>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Department</p>
              <div className="flex items-center gap-2">
                <FaBuilding className="text-orange-500" />
                <span className="font-bold text-gray-800">{timesheet.department}</span>
              </div>
            </div>
          </div>

          <div className={`flex items-center justify-between p-4 rounded-2xl border-2 ${getStatusClasses(timesheet.status)}`}>
            <div className="flex items-center gap-3">
              {timesheet.status === 'approved' ? <FaCheckCircle size={20}/> : <FaHourglassHalf size={20}/>}
              <span className="font-black uppercase tracking-tight">{timesheet.status}</span>
            </div>
            <div className="text-right text-[10px] font-bold uppercase opacity-60">
              Submitted: {moment(timesheet.createdAt).format('LL')}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Project</th>
                  <th className="px-4 py-3 text-right">Hrs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 text-sm">
                {timesheet.entries?.map((entry, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-gray-500">{moment(entry.date).format('MMM DD')}</td>
                    <td className="px-4 py-3 font-bold text-orange-600">{entry.project}</td>
                    <td className="px-4 py-3 text-right font-black">{entry.hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer with Approval Logic */}
        <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            
            {/* Show actions ONLY if user is NOT the owner and it's pending */}
            {isApprover && timesheet.status === 'pending' && !isOwnTimesheet ? (
              <div className="w-full">
                {showRejectInput ? (
                  <div className="space-y-3 w-full animate-in slide-in-from-bottom-2">
                    <textarea
                      className="w-full p-3 border-2 border-red-100 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
                      placeholder="Enter rejection reason..."
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                    />
                    <div className="flex justify-end gap-3">
                      <button onClick={() => setShowRejectInput(false)} className="text-gray-500 font-bold text-sm">Cancel</button>
                      <button 
                        onClick={() => onAction(timesheet._id, 'rejected', rejectionReason)}
                        disabled={!rejectionReason.trim()}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold disabled:opacity-50"
                      >
                        Confirm Rejection
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex w-full justify-end gap-4">
                    <button onClick={() => setShowRejectInput(true)} className="px-6 py-3 bg-white border-2 border-red-100 text-red-600 rounded-xl font-bold hover:bg-red-50">
                      Reject
                    </button>
                    <button onClick={() => {console.log("Approve button clicked for ID:", timesheet._id); onAction(timesheet._id, 'approved')}} className="px-8 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 shadow-lg">
                      Approve Timesheet
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex w-full items-center justify-between">
                {isOwnTimesheet && timesheet.status === 'pending' && (
                   <span className="flex items-center gap-2 text-orange-600 font-bold text-sm bg-orange-50 px-4 py-2 rounded-lg">
                     <FaInfoCircle /> You cannot approve your own submission.
                   </span>
                )}
                <button onClick={onClose} className="px-8 py-3 rounded-xl font-bold text-gray-700 hover:bg-gray-200 border border-gray-300 ml-auto">
                  Close Preview
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimesheetApproverView;